/**
 * Login page — multiple ways in, all landing on the same Hotline session:
 *   - email + password against the local JWT provider (default),
 *   - "Continue with Google" (Google Identity Services → POST /auth/google),
 *   - passwordless magic-link ("email me a sign-in link" → POST /auth/magic-link).
 *
 * Google + magic-link mint the same Bearer tokens as password login, so they
 * flow through the same `client-core` path. On success we route to the user's
 * role home (the authenticated guard at the top of this component redirects).
 *
 * Visuals follow the Claude Design `Login` screen: a brand mark with a pulsing
 * live-dot over the "HOTLINE / Operator console" lockup, a surface card holding
 * the icon-led fields, a primary block submit, an "or" divider and the SSO area.
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext.js';
import { GoogleSignInButton } from '../auth/GoogleSignInButton.js';
import { homePathForRole } from '../auth/guards.js';
import { GOOGLE_CLIENT_ID } from '../config.js';
import { Alert, Button, Field, Spinner } from '../components/ui/index.js';
import { inputClasses } from '../components/ui/Input.js';
import { Lock, Mail } from '../components/ui/icons.js';
import { cn } from '../components/ui/cn.js';
import { toMessage } from '../lib/useAsync.js';

type Mode = 'password' | 'magic';

export default function LoginPage(): ReactElement {
  const { status, principal, login, loginWithGoogle, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  // Already signed in → bounce to home (or the originally-requested path).
  if (status === 'authenticated' && principal) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? homePathForRole(principal.role)} replace />;
  }

  const onSubmitPassword = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login({ email, password });
      navigate(homePathForRole(undefined), { replace: true });
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmitMagic = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await requestMagicLink(email);
      setMagicSent(true);
    } catch (err) {
      setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogleCredential = async (credential: string): Promise<void> => {
    setError(null);
    try {
      await loginWithGoogle(credential);
      // State flips to authenticated → the guard above redirects on re-render.
    } catch (err) {
      setError(toMessage(err));
    }
  };

  return (
    <div
      className="grid min-h-screen place-items-center p-6"
      style={{
        background:
          'radial-gradient(120% 90% at 50% -10%, var(--color-primary-soft), var(--color-bg) 60%)',
      }}
    >
      <div className="w-full max-w-[380px]">
        {/* Brand lockup: glowing mark + pulsing live-dot, wordmark, tagline. */}
        <div className="mb-[30px] flex flex-col items-center gap-4">
          <span
            aria-hidden="true"
            className="grid h-[46px] w-[46px] place-items-center rounded-[14px] bg-primary shadow-[0_0_28px_-4px_var(--hl-primary)]"
          >
            <span className="h-[13px] w-[13px] rounded-full bg-white motion-safe:animate-pulse" />
          </span>
          <div className="text-center">
            <h1 className="text-[17px] font-extrabold tracking-[0.18em] text-text">HOTLINE</h1>
            <p className="text-[13px] text-muted">Operator console</p>
          </div>
        </div>

        {magicSent ? (
          <div className="flex flex-col gap-[15px] rounded-xl border border-border bg-surface p-[26px] shadow-lg">
            <Alert tone="info">
              Check your inbox — we sent a one-time sign-in link to <strong>{email}</strong>. It
              expires shortly and works once.
            </Alert>
            <Button
              type="button"
              variant="secondary"
              size="block"
              className="h-10"
              onClick={() => {
                setMagicSent(false);
                setMode('password');
                setError(null);
              }}
            >
              Back to sign in
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-[15px] rounded-xl border border-border bg-surface p-[26px] shadow-lg"
            onSubmit={mode === 'password' ? onSubmitPassword : onSubmitMagic}
            aria-label="Sign in"
          >
            {error ? <Alert>{error}</Alert> : null}

            <Field label="Email" htmlFor="email">
              <div className="relative">
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-faint"
                >
                  <Mail size={15} />
                </span>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={cn(inputClasses, 'h-[34px] py-2 pl-[33px]')}
                />
              </div>
            </Field>

            {mode === 'password' ? (
              <Field label="Password" htmlFor="password">
                <div className="relative">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-faint"
                  >
                    <Lock size={15} />
                  </span>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={cn(inputClasses, 'h-[34px] py-2 pl-[33px]')}
                  />
                </div>
              </Field>
            ) : null}

            <Button type="submit" variant="primary" size="block" className="h-10" disabled={submitting}>
              {submitting ? (
                <Spinner size="sm" label={mode === 'password' ? 'Signing in…' : 'Sending link…'} />
              ) : mode === 'password' ? (
                'Sign in'
              ) : (
                'Email me a sign-in link'
              )}
            </Button>

            <div className="flex items-center gap-3 text-[11px] font-semibold text-faint">
              <span className="h-px flex-1 bg-border" />
              or
              <span className="h-px flex-1 bg-border" />
            </div>

            {GOOGLE_CLIENT_ID ? (
              <GoogleSignInButton onCredential={onGoogleCredential} onError={setError} />
            ) : null}

            {mode === 'password' ? (
              <Button
                type="button"
                variant="secondary"
                size="block"
                className="h-10"
                disabled={submitting}
                onClick={() => {
                  setMode('magic');
                  setError(null);
                }}
              >
                <Mail size={16} />
                Email me a sign-in link
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                size="block"
                className="h-10"
                disabled={submitting}
                onClick={() => {
                  setMode('password');
                  setError(null);
                }}
              >
                <Lock size={16} />
                Use a password instead
              </Button>
            )}
          </form>
        )}

        <p className="mt-[18px] text-center text-xs text-faint">
          Trouble signing in?{' '}
          <a href="#" className="font-semibold text-muted hover:text-text">
            Contact your admin
          </a>
        </p>
      </div>
    </div>
  );
}
